import React, { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { enforceHTTPS } from "./utils/security";

import RedirectHandler from "./components/utils/RedirectHandler";
import Header from "./components/Layout/Header";
import Footer from "./components/Layout/Footer";
import { ToastContainer } from "react-toastify";
import RemoveTrailingSlash from "./components/utils/RemoveTrailingSlash";
import { AlternativePagesData } from "./components/Data/AlternativeDynamic";

const Home = lazy(() => import("./pages/HomePage"));

const Vids = lazy(() => import("./pages/Vids"));
const ScheduleDemo = lazy(() => import("./pages/ScheduleDemo"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Landingpage = lazy(() => import("./pages/Landing/Landingpages"));
const ThankYouPage = lazy(() => import("./pages/ThankYou"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const SecLandingPage = lazy(() => import("./pages/Landing/SecLandingPage"));
const GLandingPage = lazy(() => import("./pages/Landing/GAdsLandingPage"));
const HospitalityQSRLandingPage = lazy(
  () => import("./pages/HospitalityQSRLandingPage"),
);
const BPOITESLandingPage = lazy(() => import("./pages/BPOITESLandingPage"));
const LogisticsSupplyChainLandingPage = lazy(
  () => import("./pages/LogisticsSupplyChainLandingPage"),
);
const BFSILandingPage = lazy(() => import("./pages/BFSILandingPage"));
const InsuranceLandingPage = lazy(() => import("./pages/InsuranceLandingPage"));
// const AdsLandingPage = lazy(() => import("./pages/Landing/AdsLandingPages"));
const LpLandingPagesec = lazy(
  () => import("./pages/Landing/AdsLandingPageSec"),
);
const LpLandingPageThird = lazy(
  () => import("./pages/Landing/AdsLandingPagesThird"),
);
const LpthankYou = lazy(
  () => import("./components/Pages/LandingPage/adsSecLanding/AdsThankuPage"),
);
const AlternativeDynamicPage = lazy(
  () => import("./pages/Alternative/AlternativeDynamic"),
);
const CaseStudyDynamicPage = lazy(
  () => import("./pages/case-study/CaseStudyDynamic"),
);
const CaseStudyLandingPage = lazy(
  () => import("./pages/case-study/CaseStudyLanding"),
);
const RecruiterLandingPages = lazy(
  () => import("./pages/Landing/RecruiterLandingPages"),
);
const SignUpPages = lazy(() => import("./pages/Signup"));
const LoginPages = lazy(() => import("./pages/Login"));
const AuthorSinglePages = lazy(
  () => import("./pages/Blog/Author/AuthorSinglePage"),
);
const BlogLandingPage = lazy(() => import("./pages/Blog/BlogLandingPages"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const BlogSinglePage = lazy(() => import("./pages/Blog/BlogSinglePages"));
const TestSolution = lazy(() => import("./pages/Solutions/SingleSolutionPage"));
const AboutPage = lazy(() => import("./pages/About/AboutPage"));
const ConatctNew = lazy(() => import("./pages/contact/ContactPage"));
// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Page transition wrapper
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

// Loading screen
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center">
      <div className="text-accent relative w-16 h-16">
        <svg className="animate-spin h-16 w-16" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <p className="mt-4 text-lg font-medium text-accent">Loading...</p>
    </div>
  </div>
);

// Routes
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageTransition>
              <Home />
            </PageTransition>
          }
        />
        <Route
          path="/solutions/:solutions"
          element={
            <PageTransition>
              <TestSolution />
            </PageTransition>
          }
        />

        <Route
          path="/ai-first-chro/free-copy-reserved"
          element={
            <PageTransition>
              <LpthankYou />
            </PageTransition>
          }
        />
        <Route
          path="/ai-first-chro-g/free-copy-reserved"
          element={
            <PageTransition>
              <LpthankYou />
            </PageTransition>
          }
        />
        <Route
          path="/about"
          element={
            <PageTransition>
              <AboutPage />
              {/* <Highlights />
              <CTA /> */}
            </PageTransition>
          }
        />
        <Route
          path="/contact"
          element={
            <PageTransition>
              <ConatctNew />
            </PageTransition>
          }
        />
        <Route
          path="/schedule-demo"
          element={
            <PageTransition>
              <ScheduleDemo />
            </PageTransition>
          }
        />
        <Route
          path="/hospitality-qsr-hiring-automation"
          element={
            <PageTransition>
              <HospitalityQSRLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/bpo-ites-hiring-automation"
          element={
            <PageTransition>
              <BPOITESLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/logistics-supply-chain-hiring-automation"
          element={
            <PageTransition>
              <LogisticsSupplyChainLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/bfsi-hiring-automation"
          element={
            <PageTransition>
              <BFSILandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/insurance-hiring-automation"
          element={
            <PageTransition>
              <InsuranceLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/ai-first-chro"
          element={
            <PageTransition>
              <LpLandingPagesec />
            </PageTransition>
          }
        />
        <Route
          path="/ai-first-chro-g"
          element={
            <PageTransition>
              <GLandingPage />
            </PageTransition>
          }
        />

        <Route
          path="/blog"
          element={
            <PageTransition>
              <BlogLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/blog/:blog"
          element={
            <PageTransition>
              <BlogSinglePage />
            </PageTransition>
          }
        />
        <Route
          path="/videos"
          element={
            <PageTransition>
              <Vids />
            </PageTransition>
          }
        />
        <Route
          path="/author/:author"
          element={
            <PageTransition>
              <AuthorSinglePages />
            </PageTransition>
          }
        />
        <Route
          path="/case-studies"
          element={
            <PageTransition>
              <CaseStudyLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/case-studies/:casestudy"
          element={
            <PageTransition>
              <CaseStudyDynamicPage />
            </PageTransition>
          }
        />
        <Route
          path="/privacy"
          element={
            <PageTransition>
              <PrivacyPolicy />
            </PageTransition>
          }
        />
        <Route
          path="/cookies"
          element={
            <PageTransition>
              <CookiePolicy />
            </PageTransition>
          }
        />
        <Route
          path="/terms"
          element={
            <PageTransition>
              <TermsOfService />
            </PageTransition>
          }
        />
        <Route
          path="/end-to-end-hiring-solution-for-enterprises"
          element={
            <PageTransition>
              <Landingpage />
            </PageTransition>
          }
        />

        <Route
          path="/thank-you"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/ai-first-chro/discovery-call-booked"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/people-matters-techhr-india"
          element={
            <PageTransition>
              <LpLandingPageThird />
            </PageTransition>
          }
        />
        <Route
          path="/people-matters-techhr-india/thank-you"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/end-to-end-hiring-solution-for-enterprises/thank-you"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/solutions"
          element={
            <PageTransition>
              <SolutionPage />
            </PageTransition>
          }
        />
        <Route
          path="/automated-employee-background-verification"
          element={
            <PageTransition>
              <SecLandingPage />
            </PageTransition>
          }
        />
        <Route
          path="/automated-employee-background-verification/thank-you"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/competitor/lead-submit"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        {Object.keys(AlternativePagesData).map((key) => {
          return (
            <Route
              key={key}
              path={key}
              element={
                <PageTransition>
                  <AlternativeDynamicPage alternativePage={key} />
                </PageTransition>
              }
            />
          );
        })}

        <Route
          path="/agentic-ai-recruiters-sheet"
          element={
            <PageTransition>
              <RecruiterLandingPages />
            </PageTransition>
          }
        />
        <Route
          path="/agentic-ai-recruiters-sheet/thank-you"
          element={
            <PageTransition>
              <ThankYouPage />
            </PageTransition>
          }
        />
        <Route
          path="/signup"
          element={
            <PageTransition>
              <SignUpPages />
            </PageTransition>
          }
        />
        <Route
          path="/login"
          element={
            <PageTransition>
              <LoginPages />
            </PageTransition>
          }
        />
        <Route
          path="*"
          element={
            <PageTransition>
              <ErrorPage />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

// ✅ FIXED App — useLocation now inside Router
function AppWrapper() {
  useEffect(() => {
    enforceHTTPS();

    if (process.env.REACT_APP_XSS_PROTECTION === "true") {
      const metaXSS = document.createElement("meta");
      metaXSS.httpEquiv = "X-XSS-Protection";
      metaXSS.content = "1; mode=block";
      document.head.appendChild(metaXSS);

      const metaNoSniff = document.createElement("meta");
      metaNoSniff.httpEquiv = "X-Content-Type-Options";
      metaNoSniff.content = "nosniff";
      document.head.appendChild(metaNoSniff);

      const metaFrameOptions = document.createElement("meta");
      metaFrameOptions.httpEquiv = "X-Frame-Options";
      metaFrameOptions.content = "DENY";
      document.head.appendChild(metaFrameOptions);
    }
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 50,
      delay: 0,
    });
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const hideNavbarRoutes = [
    "/end-to-end-hiring-solution-for-enterprises",
    "/automated-background-verification",
    "/agentic-ai-recruiters-sheet",
    "/ads",
    "/ai-first-chro",
    "/ai-first-chro-g",
    "/automated-employee-background-verification",
    "/people-matters-techhr-india",
  ];
  const shouldHideNavbar = hideNavbarRoutes.includes(location.pathname);

  return (
    <>
      <RemoveTrailingSlash />
      <RedirectHandler />
      <ToastContainer position="top-right" autoClose="4000" />
      <ScrollToTop />
      <div className="flex flex-col">
        {!shouldHideNavbar && <Header />}
        <main className="flex-grow pt-16 lg:pt-12">
          <Suspense fallback={<Loading />}>
            <AnimatedRoutes />
          </Suspense>
        </main>
        {!shouldHideNavbar && <Footer />}
      </div>
    </>
  );
}

export default AppWrapper;
